// Generated by TaroBase SDK Generator: 2026-07-18T20:30:00.103Z
// Collection: users/$userId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * User profile keyed by Solana wallet address. Stores app role (household, company, or agent) since Poof has no built-in roles, plus display info. Public read for homepage aggregate counters.
 */

export interface UsersRequest {
  role: string;
  displayName: string;
  city: string;
  avatarUrl?: string;
  bio?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UsersResponse {
  role: string;
  displayName: string;
  city: string;
  avatarUrl?: string;
  bio?: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Users operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUsers(userId: string, data: UsersRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `users/${userId}`, document: doc };
}

/**
 * Authenticated users only. Document ID ($userId) must equal caller wallet (@user.address), so a user can only create their own profile. createdAt is a required Unix-seconds timestamp; role must be one of household|company|agent (enforced by app UI). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUsers(userId: string, data: UsersRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`users/${userId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Users: ${formatError(error)}`);
    return false;
  }
}

export type UsersRequestUpdate = Partial<UsersRequest>;

/**
 * Build a Users update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateUsers(userId: string, data: UsersRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `users/${userId}`, document: doc };
}

/**
 * Only the profile owner (@user.address == $userId) may update their profile. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateUsers(userId: string, data: UsersRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`users/${userId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Users: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone (including unauthenticated homepage visitors) can read profiles to compute aggregate counters. All fields exposed.
   (Get Single Item)
 */
export async function getUsers(userId: string): Promise<UsersResponse | null> {
  try {
    const result = await get(`users/${userId}`);
    return result as UsersResponse | null;
  } catch (error) {
    console.error(`Error getting Users: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Users document. (
  Read Operation Details: Anyone (including unauthenticated homepage visitors) can read profiles to compute aggregate counters. All fields exposed.
  )
 */
export function subscribeUsers(callback: (data: UsersResponse | null) => void, userId: string): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UsersResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Users at path '${`users/${userId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Users items from collection users
 
  Read Operation Details: Anyone (including unauthenticated homepage visitors) can read profiles to compute aggregate counters. All fields exposed.
  
 */
export async function getManyUsers(filter: string = ""): Promise<UsersResponse[]> {
  try {
    const results = await get("users", { prompt: filter });
    return Array.isArray(results) ? results as UsersResponse[] : [];
  } catch (error) {
    console.error(`Error getting Users collection at path 'users': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Users collection at users
 
  Read Operation Details: Anyone (including unauthenticated homepage visitors) can read profiles to compute aggregate counters. All fields exposed.
  
 */
export function subscribeManyUsers(callback: (data: UsersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("users", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Users collection at path '${"users"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Users items from collection users
 
  Read Operation Details: Anyone (including unauthenticated homepage visitors) can read profiles to compute aggregate counters. All fields exposed.
  
 */
export async function getAllUsers(filter: string = ""): Promise<UsersResponse[]> {
  try {
    const results = await get("users", { prompt: filter });
    return Array.isArray(results) ? results as UsersResponse[] : [];
  } catch (error) {
    console.error(`Error getting Users collection at path 'users': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Users collection at users
 
  Read Operation Details: Anyone (including unauthenticated homepage visitors) can read profiles to compute aggregate counters. All fields exposed.
  
 */
export function subscribeAllUsers(callback: (data: UsersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("users", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Users collection at path '${"users"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Users items in collection users.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUsers(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("users", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Users at path 'users': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Users items in collection users.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUsers(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("users", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Users at path 'users': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the profile owner (@user.address == $userId) may delete their profile.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUsers(userId: string): Promise<boolean> {
  try {
    const succeeded = await set(`users/${userId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Users: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Users for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUsers(userId: string): DocumentOperation {
  return { path: `users/${userId}`, document: null };
}

